import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, FileWarning, Loader2, AlertTriangle } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { LEGAL_PAGES } from "@/data/legalContent";
import { LEGAL_ADDENDA } from "@/data/legalAddenda";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Maps our public URL slugs to the RelayRigs app's live Legal Center document keys
// (GET /api/legal/documents/{key}). Anything not listed here — and not in
// LEGAL_PAGES — is unknown and redirects home.
const SLUG_TO_UPSTREAM_KEY = {
  "terms-of-service": "terms",
  "privacy-policy": "privacy",
  "driver-agreement": "contractor-agreement",
  "payment-terms": "refund-policy",
  "insurance-disclosures": "insurance-coverage",
  "cookie-policy": "cookie-policy",
  "acceptable-use-policy": "acceptable-use",
  "data-retention": "data-retention",
};
const REMARK_PLUGINS = [remarkGfm];

export default function LegalPage() {
  const { slug } = useParams();
  const localPage = LEGAL_PAGES[slug];
  const upstreamKey = SLUG_TO_UPSTREAM_KEY[slug];
  const [status, setStatus] = useState(localPage ? "local" : upstreamKey ? "loading" : "notfound");
  const [doc, setDoc] = useState(null);

  useEffect(() => {
    if (!upstreamKey) return;
    setStatus("loading");
    setDoc(null);
    axios
      .get(`${API}/legal/documents/${upstreamKey}`)
      .then((res) => {
        if (!res.data?.markdown) throw new Error("empty");
        setDoc(res.data);
        setStatus("loaded");
      })
      .catch(() => setStatus("error"));
  }, [slug, upstreamKey]);

  if (status === "notfound") return <Navigate to="/" replace />;

  return (
    <div data-testid="legal-page" className="min-h-screen bg-white">
      <Header />
      <div className="max-w-3xl mx-auto px-5 md:px-8 pt-32 pb-24">
        <Link to="/" data-testid="legal-back-link" className="inline-flex items-center gap-2 text-sm font-bold text-rr-navy mb-8 hover:text-rr-orange transition-colors">
          <ArrowLeft size={16} /> Back to RelayRigs
        </Link>

        {status === "loading" && (
          <div data-testid="legal-loading-state" className="flex items-center gap-3 text-rr-muted py-20 justify-center">
            <Loader2 size={20} className="animate-spin" /> Loading document…
          </div>
        )}

        {status === "error" && (
          <div data-testid="legal-error-state" className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-6">
            <AlertTriangle size={20} className="text-red-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-rr-navy">This document is temporarily unavailable.</p>
              <p className="text-sm text-rr-muted mt-1">
                The RelayRigs Legal Center couldn't be reached. Please try again shortly, or contact{" "}
                <a href="mailto:legal@relayrigs.com" className="underline hover:text-rr-orange">legal@relayrigs.com</a>.
              </p>
            </div>
          </div>
        )}

        {(status === "local" || status === "loaded") && (
          <>
            <div data-testid="legal-draft-banner" className="flex items-start gap-3 bg-rr-orange/10 border border-rr-orange/30 rounded-xl p-4 mb-8">
              <FileWarning size={18} className="text-rr-orange shrink-0 mt-0.5" />
              <p className="text-xs text-rr-navy/80 leading-relaxed">
                <span className="font-bold">Draft — Subject to Legal Review.</span> This page is a placeholder prepared ahead of RelayRigs' public launch and has not yet been reviewed by legal counsel. It does not constitute a final or binding agreement.
              </p>
            </div>

            {status === "local" ? (
              <>
                <h1 data-testid="legal-page-title" className="font-heading text-3xl md:text-4xl font-bold text-rr-navy">{localPage.title}</h1>
                <p data-testid="legal-page-updated" className="text-xs text-rr-muted mt-2">Last updated: {localPage.lastUpdated} (Draft)</p>

                <div className="mt-10 space-y-8">
                  {localPage.sections.map((s, i) => (
                    <div key={s.heading} data-testid={`legal-section-${i}`}>
                      <h2 className="font-heading text-lg font-bold text-rr-navy mb-2">{s.heading}</h2>
                      {Array.isArray(s.body) ? (
                        <ul className="list-disc pl-5 space-y-1.5 text-sm text-rr-muted leading-relaxed">
                          {s.body.map((b) => (
                            <li key={b}>{b}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-rr-muted leading-relaxed">{s.body}</p>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <h1 data-testid="legal-page-title" className="font-heading text-3xl md:text-4xl font-bold text-rr-navy">{doc.title}</h1>
                <p data-testid="legal-page-updated" className="text-xs text-rr-muted mt-2">Version {doc.version} (Draft)</p>

                <div data-testid="legal-markdown-content" className="mt-10 prose prose-sm md:prose-base max-w-none prose-headings:font-heading prose-headings:text-rr-navy prose-a:text-rr-orange prose-strong:text-rr-navy prose-table:text-sm">
                  <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{doc.markdown}</ReactMarkdown>
                </div>

                {LEGAL_ADDENDA[slug] && (
                  <div data-testid="legal-addendum-block" className="mt-12 pt-8 border-t border-rr-line">
                    <h2 className="font-heading text-xl font-bold text-rr-navy mb-1">RelayRigs Website Addendum</h2>
                    <p className="text-xs text-rr-muted mb-6">The following additional terms apply specifically to relayrigs.com and supplement the document above.</p>
                    <div className="space-y-8">
                      {LEGAL_ADDENDA[slug].map((s, i) => (
                        <div key={s.heading} data-testid={`legal-addendum-section-${i}`}>
                          <h3 className="font-heading text-lg font-bold text-rr-navy mb-2">{s.heading}</h3>
                          {Array.isArray(s.body) ? (
                            <ul className="list-disc pl-5 space-y-1.5 text-sm text-rr-muted leading-relaxed">
                              {s.body.map((b) => (
                                <li key={b}>{b}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-rr-muted leading-relaxed">{s.body}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
